<template>
  <div class="view-wrapper">
    <span v-if="!isAuthenticated">
      Bitte anmelden, um die Applikation zu verwenden.
    </span>

    <template v-else>
      <BackButton />

      <h2 class="page-title">Lagerstand aller Artikel im Ausgabeautomat</h2>

      <Card class="table-card">
        <template #content>
          <div class="table-toolbar">
            <IconField icon-position="right" class="global-search">
              <InputText
                v-model="globalFilterValue"
                placeholder="Suchbegriff eingeben..."
              />
              <InputIcon
                v-if="globalFilterValue"
                class="pi pi-times"
                @click="clearGlobalSearch"
                style="cursor: pointer"
              />
            </IconField>
          </div>

          <div v-if="loadError" class="error">
            {{ loadError }}
          </div>

          <DataTable
            :value="inventoryView"
            v-model:filters="filters"
            :global-filter-fields="globalFilterFields"
            data-key="rowKey"
            table-layout="auto"
            paginator
            :rows="10"
            :rows-per-page-options="[5, 10, 20, 50]"
            scrollable
            show-gridlines
            striped-rows
            removable-sort
            :loading="isLoading"
          >
            <template #empty>
              <div style="text-align: center; padding: 0.5rem">
                Keine Daten gefunden.
              </div>
            </template>
            <Column
              field="productId"
              header="Artikel"
              sortable
              filter
              :show-filter-menu="false"
              filter-placeholder="Filtern..."
            />

            <Column
              field="productName"
              header="Bezeichnung"
              sortable
              filter
              :show-filter-menu="false"
              filter-placeholder="Filtern..."
            />

            <Column
              field="stockLevel"
              header="Bestand"
              sortable
              filter
              :show-filter-menu="false"
              filter-placeholder="Filtern..."
            />
          </DataTable>
        </template>
      </Card>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref, watch } from "vue";
  import { useIsAuthenticated } from "@/composition-api/useIsAuthenticated";
  import Card from "primevue/card";
  import DataTable from "primevue/datatable";
  import Column from "primevue/column";
  import InputText from "primevue/inputtext";
  import IconField from "primevue/iconfield";
  import InputIcon from "primevue/inputicon";
  import BackButton from "@/components/BackButton.vue";
  import { useBackendApi } from "@/composition-api/useBackendApi";
  import type { Stock } from "@apis/backend";

  const isAuthenticated = useIsAuthenticated();
  const apis = useBackendApi();

  const isLoading = ref(false);
  const loadError = ref<string | null>(null);
  const inventory = ref<Stock[]>([]);

  const inventoryView = computed(() =>
    inventory.value.map((i) => ({
      ...i,
      rowKey: i.productId,
      productId: i.productId?.replace(/^BDS/, "BBW ")?.replace(/^OY/, "OY "),
    })),
  );

  const globalFilterValue = ref("");

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filters = ref<Record<string, any>>({
    global: { value: null, matchMode: "contains" },
    productId: { value: null, matchMode: "contains" },
    productName: { value: null, matchMode: "contains" },
    stockLevel: { value: null, matchMode: "contains" },
  });

  const globalFilterFields = ["productId", "productName", "stockLevel"];

  const clearGlobalSearch = () => {
    globalFilterValue.value = "";
  };

  watch(globalFilterValue, (v) => {
    filters.value.global.value = v?.trim() || null;
  });

  onMounted(() =>
    apis.callWhenTokenInitialized(() => {
      void loadInventory();
    }),
  );

  async function loadInventory() {
    try {
      isLoading.value = true;
      loadError.value = null;

      const data = await apis.reporting.fetchStock();
      inventory.value = data ?? [];
    } catch (error) {
      console.error("Fehler beim Laden der Lagerbestandsdaten:", error);
      loadError.value = "Fehler beim Laden der Lagerbestandsdaten.";
      inventory.value = [];
    } finally {
      isLoading.value = false;
    }
  }
</script>

<style scoped lang="scss">
  .view-wrapper {
    width: 99%;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    font-family: inherit;
  }

  .page-title {
    margin: 1rem 0 0;
  }

  .table-card {
    margin-top: 1.5rem;
    width: 100%;
  }

  .table-toolbar {
    display: flex;
    justify-content: center;
    margin-bottom: 0.75rem;
  }

  .global-search :deep(input) {
    min-width: 22rem;
    font-size: 0.95rem;
    padding: 0.4rem 0.6rem;
    border: 1px solid #c5c5c5;
    border-radius: 4px;
    background: #fff;
    box-sizing: border-box;
  }

  .error {
    width: 100%;
    margin-bottom: 0.75rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid #f3c2c2;
    background: #fff3f3;
    border-radius: 6px;
  }
</style>
